//@ts-nocheck
import React, { useState } from "react";
import dayjs from "dayjs";
import get from "lodash/get";
import Logger from "lib/Logger";
import { receiveAccessPolicy } from "lib/actions/storages";
import { showPopupFlag } from "lib/actions/userInterface";
import AccessControlAPI from "lib/api/accessControl";
import AccessScopes from "lib/constants/AccessScopes";
import store, { useSelector } from "store";
import { selectStorageByID } from "store/reducers";
import { PrimaryButton, SecondaryButton } from "components/Buttons";
import { DataGrid, DataItem } from "components/DataGrid";
import { CheckboxInput, TextInput } from "components/Input";
import { DateTimeInput } from "components/Input";
import RadioInput from "components/Input/RadioInput";
import { Paragraph } from "components/Typography";
import FieldGroup from "components/form/FieldGroup/FieldGroup";
import ProjectsInput from "components/inputs/ProjectsInput";
import SelectAccessScope from "components/inputs/SelectAccessScope";
import SelectKeycard from "components/inputs/SelectKeycard";
import SelectRoleInput from "components/inputs/SelectRoleInput";
import UsersInput from "components/inputs/UsersInput";
import { Modal, ModalContent, ModalFooter, ModalHeader } from "components/modals/Modal";
import { VSpace } from "components/shared";
import { drawerAccessWarnings } from "components/shared/features/storages/utils/drawerAccess.utility";
import parentStyle from "../RootModal/modal.module.scss";
import style from "./modal.module.scss";

const CreateAccessRuleModal = ({ modal, onClose }) => {
  const [submitting, isSubmitting] = useState(false);
  const [values, setValues] = useState({
    enabled: true,
    scope: AccessScopes.resolveItem(AccessScopes.ROLE),
    valueString: "",
    valueInt: "",
    allowKeycard: true,
    allowMobileApp: false,
    activeDate: null,
    expireDate: null,
    allDrawers: true,
    allowedDrawers: [],
  });

  const { session, policy, storageId } = modal.props;
  const storage = useSelector(selectStorageByID(storageId));

  const handleSubmit = async (e) => {
    isSubmitting(true);
    try {
      e.preventDefault();
      const newPolicy = {
        ...policy,
      };
      const newGrant = {
        enabled: values.enabled,
        rule: {
          scope: values.scope.value,
          valueString: get(values, "valueString.id", values.valueString),
          valueInt: get(values, "valueInt.id", parseInt(values.valueInt)),
        },
        allowKeycard: values.allowKeycard,
        allowMobileApp: values.allowMobileApp,
        activeDate: values.activeDate ? dayjs(values.activeDate).toISOString() : null,
        expireDate: values.expireDate ? dayjs(values.expireDate).toISOString() : null,
        allDrawers: values.allDrawers,
        allowedDrawers: values.allDrawers ? [] : values.allowedDrawers,
      };
      newPolicy.grants = [...newPolicy.grants, newGrant];
      const data = await AccessControlAPI.updatePolicy(newPolicy.id, newPolicy);
      store.dispatch(receiveAccessPolicy(data));
      store.dispatch(
        showPopupFlag({
          appearance: "success",
          title: "Created Access Rule",
        }),
      );
      onClose();
    } catch (err) {
      Logger.error(err);
      store.dispatch(
        showPopupFlag({
          appearance: "error",
          title: "Failed to Create Access Rule",
        }),
      );
    }
    isSubmitting(false);
  };

  const handleChange = (name, value) => {
    setValues({
      ...values,
      [name]: value,
    });
  };

  const handleAllDrawersChange = (option) => {
    // RadioOption reports a click on the already-selected option as a deselect (null). Drawer
    // access is a two-state choice with no legal third state, so there is nothing to apply:
    // reading null as "all drawers" would silently widen the rule being written.
    if (!option) {
      return;
    }
    handleChange("allDrawers", option.value);
  };

  const handleDrawerToggle = (drawerValue) => {
    const allowedDrawers = values.allowedDrawers.includes(drawerValue)
      ? values.allowedDrawers.filter((value) => value !== drawerValue)
      : [...values.allowedDrawers, drawerValue];
    handleChange("allowedDrawers", allowedDrawers);
  };

  const renderDrawerAccess = () => {
    if (!storage?.config?.maglockEnabled) {
      return null;
    }

    const warnings = drawerAccessWarnings(values.allDrawers, values.allowedDrawers, storage.drawers);

    return (
      <FieldGroup label="Drawers allowed">
        <RadioInput
          options={[
            { value: true, name: "All drawers" },
            { value: false, name: "Selected drawers" },
          ]}
          value={values.allDrawers}
          onChange={handleAllDrawersChange}
        />
        {values.allDrawers === false && (
          <VSpace gap="gap-2">
            {storage.drawers.map((drawer) => (
              <CheckboxInput
                key={drawer.id}
                input={{ name: `drawer-${drawer.value}` }}
                label={`Drawer ${drawer.value}${drawer.name ? ` — ${drawer.name}` : ""}`}
                value={values.allowedDrawers.includes(drawer.value)}
                onChange={() => handleDrawerToggle(drawer.value)}
              />
            ))}
          </VSpace>
        )}
        {warnings.map((warning) => (
          <Paragraph key={warning} className="text-red-500">
            {warning}
          </Paragraph>
        ))}
      </FieldGroup>
    );
  };

  const renderScope = () => {
    const scope = values.scope;
    if (!scope) {
      return null;
    }
    switch (scope.value) {
      case AccessScopes.GROUP:
        return (
          <TextInput
            input={{
              name: "valueString",
              placeholder: "Enter group name",
            }}
            value={values.valueString}
            onChange={handleChange}
          />
        );
      case AccessScopes.ROLE:
        return (
          <SelectRoleInput
            name="valueInt"
            value={values.valueInt}
            onChange={(_, value) => handleChange("valueInt", value.id)}
          />
        );
      case AccessScopes.PROJECT:
        return (
          <ProjectsInput
            name="valueInt"
            value={values.valueInt}
            onChange={(_, value) => handleChange("valueInt", value.id)}
          />
        );
      case AccessScopes.USER:
        return (
          <UsersInput
            name="valueInt"
            value={values.valueInt}
            onChange={(_, value) => handleChange("valueInt", value.id)}
          />
        );
      case AccessScopes.KEYCARD:
        return (
          <SelectKeycard
            name="valueString"
            value={values.valueString}
            onChange={(_, value) => handleChange("valueString", value)}
          />
        );
      default:
        return null;
    }
  };

  const valid =
    values.scope && (values.valueString || values.valueInt) && (values.allowKeycard || values.allowMobileApp);

  return (
    <Modal className={parentStyle.wideModal}>
      <ModalHeader title="Grant access" />
      <ModalContent>
        <VSpace>
          <FieldGroup label="Access Scope">
            <SelectAccessScope name="scope" value={values.scope} onChange={handleChange} />
            <br />
            {renderScope()}
          </FieldGroup>
          {values.scope.value !== AccessScopes.KEYCARD && (
            <FieldGroup label="Methods allowed">
              <Paragraph className="text-muted">Select which access methods will be active for the rule</Paragraph>
              <div className={style.methods}>
                <RadioInput
                  options={[{ value: true, name: "Keycard" }]}
                  value={values.allowKeycard}
                  onChange={(value) => handleChange("allowKeycard", value ? value.value : null)}
                />
                <RadioInput
                  options={[{ value: true, name: "Mobile app" }]}
                  value={values.allowMobileApp}
                  onChange={(value) => handleChange("allowMobileApp", value ? value.value : null)}
                />
              </div>
            </FieldGroup>
          )}
          {renderDrawerAccess()}
          <FieldGroup label="Access period">
            <DataGrid>
              <DataItem label="Active from">
                <DateTimeInput value={values.activeDate} onChange={(value) => handleChange("activeDate", value)} />
              </DataItem>
              <DataItem label="Expire on">
                <DateTimeInput value={values.expireDate} onChange={(value) => handleChange("expireDate", value)} />
              </DataItem>
            </DataGrid>
          </FieldGroup>
        </VSpace>
      </ModalContent>
      <ModalFooter>
        <PrimaryButton disabled={!valid || submitting} onClick={handleSubmit}>
          Grant access
        </PrimaryButton>
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
      </ModalFooter>
    </Modal>
  );
};

export default CreateAccessRuleModal;
